import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, FileText } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { TABS, API } from '@/components/agent-credit/shared';
import SDKPlayground from '@/components/SDKPlayground';
import RepaymentDashboard from '@/components/RepaymentDashboard';
import AlertDashboard from '@/components/AlertDashboard';
import AgentsTab from '@/components/agent-credit/AgentsTab';
import LenderPoolTab from '@/components/agent-credit/LenderPoolTab';
import EscrowTab from '@/components/agent-credit/EscrowTab';
import EvaluatorsTab from '@/components/agent-credit/EvaluatorsTab';
import RevenueTab from '@/components/agent-credit/RevenueTab';
import ArtifactsTab from '@/components/agent-credit/ArtifactsTab';
import KnowledgeTab from '@/components/agent-credit/KnowledgeTab';
import InstitutionalTab from '@/components/agent-credit/InstitutionalTab';
import CrossChainTab from '@/components/agent-credit/CrossChainTab';
import PositionsTab from '@/components/agent-credit/PositionsTab';

const TAB_COMPONENTS = {
  agents: AgentsTab,
  pool: LenderPoolTab,
  escrow: EscrowTab,
  evaluators: EvaluatorsTab,
  revenue: RevenueTab,
  artifacts: ArtifactsTab,
  knowledge: KnowledgeTab,
  institutional: InstitutionalTab,
  crosschain: CrossChainTab,
  positions: PositionsTab,
  repayment: RepaymentDashboard,
  alerts: AlertDashboard,
  sdk: SDKPlayground,
};

export default function AgentCreditDashboard() {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'agents';
  });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/agent-credit/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const ActiveComponent = TAB_COMPONENTS[activeTab];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="container mx-auto px-6 pt-28 pb-20" data-testid="agent-credit-dashboard">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <CreditCard className="w-5 h-5" />
            </div>
            <span className="text-xs font-mono text-blue-400 tracking-widest uppercase">Kojiru Agent Credit Protocol</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Agent Credit Dashboard</h1>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-gray-500">Trust-based credit infrastructure for the autonomous agent economy.</p>
            <a
              href="/kojiru-high-school-guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium hover:bg-emerald-600/20 transition-colors whitespace-nowrap"
              data-testid="high-school-guide-link"
            >
              <FileText className="w-3.5 h-3.5" />
              High School Guide (PDF)
            </a>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white/[0.02] rounded-xl p-1 border border-white/5 overflow-x-auto" data-testid="dashboard-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white border border-blue-500/20'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.03] border border-transparent'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {ActiveComponent && <ActiveComponent stats={stats} />}
          </motion.div>
        </AnimatePresence>
      </div>

      <Footer />
    </div>
  );
}
